import { FilledButton } from 'components/Custom'
import { SideBarContainer } from 'components/Custom'
import React, { useRef, useState } from 'react'
import useSidebarStore from 'store/useSidebar'
import { Box } from 'components/Material/Box'
import { SidebarHeader } from 'components/Custom/SidebarHeader/Web/index'

import { useIsMobile } from 'hooks/useIsResponsive'
import { SidebarFooter } from 'components/Custom/SidebarFooter'
import useTextStyles from 'styles/useTextStyles'
import { Typography } from 'components/Material/Typography'
import useDocumentsManagementStore from 'store/useDocumentsManagementStore'
import TrashCan from 'assets/icons/TrashCan'
import colors from 'colors'
import CheckIcon from '@mui/icons-material/Check'
import IconButton from 'components/Material/IconButton'
import { useMutation } from '@tanstack/react-query'
import { postIncludeDocument } from 'services/documentos/documentApproval'
import { ErrorResponse, HandlerErrorMessage } from 'utils/HandlerErrorMessage'
import { AxiosError, AxiosProgressEvent } from 'axios'
import { LinearProgress } from '@mui/material'
import { DocumentParamns } from 'types/documentos'
import { useSnackbarAlertContext } from 'contexts/SnackbarContext'

type UploadDocumentSidebarProps = {
  handleCallGetListDocuments: (id_documento: number | undefined) => Promise<void>
}
const UploadDocumentSidebar = ({ handleCallGetListDocuments }: UploadDocumentSidebarProps) => {
  const { closeSidebar, openSidebar } = useSidebarStore((state) => state)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { documentToUpload, setDocumentToUpload, deleteFromStage, proposal } =
    useDocumentsManagementStore((state) => state)
  const isMobile = useIsMobile()

  const [progress, setProgress] = useState(0)
  const [tryAgain, setTryAgain] = useState(false)
  const { showAlert } = useSnackbarAlertContext()

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setDocumentToUpload({ ...documentToUpload, file, salvar_arquivo: true })
    }
  }

  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const postIncludeDocumentMutation = useMutation({
    mutationFn: postIncludeDocument,
    onError: (error: AxiosError<ErrorResponse>) => {
      HandlerErrorMessage({ showAlert, error, customErrorMessage: 'Erro ao incluir documento' })
    }
  })

  const uploadImage = async () => {
    const { id_concc070, id_corcc080, file, salvar_arquivo } = documentToUpload
    const mutationData: DocumentParamns = {
      id_concc070,
      arquivo_documento: file,
      salvar_arquivo
    }

    if (id_corcc080 !== 0) {
      mutationData.id_corcc080 = id_corcc080
    }

    setProgress(0)
    try {
      await postIncludeDocumentMutation
        .mutateAsync({
          documentToUpload: mutationData,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              )
              setProgress(percentCompleted)
            }
          }
        })
        .then(async (data) => {
          if (data.id_corcc080 === 0) {
            HandlerErrorMessage({ showAlert, customErrorMessage: 'Erro ao incluir documento' })
          }
          if (data.id_corcc080 !== 0) {
            deleteFromStage(data.id_corcc080, 'delete')
          }

          await handleCallGetListDocuments(proposal?.id_documento)
        })

      setProgress(100)
      openSidebar('documents-detail')
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo', error)
      setTryAgain(true)
      setProgress(50)
    }
  }

  return (
    <SideBarContainer>
      <SidebarHeader
        title={documentToUpload.nm_documento}
        leftArrowClick={() => openSidebar('documents-detail')}
        onClick={() => closeSidebar()}
        hideCloseButton={!isMobile}
      />

      <Box className="flex justify-center flex-grow">
        <Box className="flex flex-col w-full max-w-[390px] p-5 gap-5">
          <Box className="flex flex-col gap-5">
            <Typography sx={{ ...useTextStyles.bodyMedium }}>
              Prepare um ambiente iluminado para uma boa qualidade da imagem.
            </Typography>
            <Typography sx={{ ...useTextStyles.labelLarge }}>Arquivos</Typography>
          </Box>
          {documentToUpload?.file && (
            <Box className="flex flex-col">
              <Box className="flex justify-between items-center	 px-4 mb-6">
                <CheckIcon className="text-blue-bb" />
                {documentToUpload?.file?.name && <Box>{documentToUpload.file.name}</Box>}

                <IconButton
                  className="cursor-pointer"
                  onClick={() => {
                    setDocumentToUpload({ ...documentToUpload, file: null, salvar_arquivo: false })
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''
                    }
                  }}
                >
                  <TrashCan colors={colors['blue-bb']} />
                </IconButton>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          <FilledButton
            disabled={!!documentToUpload.file}
            form="secondary"
            onClick={handleFileButtonClick}
          >
            Localizar arquivos
          </FilledButton>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      </Box>

      <SidebarFooter>
        <Box className="w-full max-w-[350px]">
          <FilledButton
            disabled={postIncludeDocumentMutation.isPending || !documentToUpload.file}
            onClick={uploadImage}
            loading={postIncludeDocumentMutation.isPending}
          >
            {tryAgain ? 'Tente novamente' : 'Incluir'}
          </FilledButton>
        </Box>
      </SidebarFooter>
    </SideBarContainer>
  )
}

export default UploadDocumentSidebar
